// v2.18.4a — Drawer PRO mobile-premium avec burger avatar 44x44 + slide-in droite.
// Glass-morphism, focus-trap léger, fermeture par tap-outside / Escape / bouton ✕.

import { useEffect, useState } from "react";

function initialsOf(name) {
  if (!name) return "??";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");
}

function maskToken(t) {
  if (!t) return "—";
  return `••••-••••-***-${String(t).slice(-4)}`;
}

export default function CourierProDrawer({ open, onClose, courier, nightMode, onToggleNightMode, onLogout }) {
  // Verrouille scroll body quand drawer ouvert.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // v2.18.5 — Toggle "Pilote auto" persisté (raccourcis embouteillage acceptés sans confirmation).
  const AUTO_KEY = "civ_courier_pilote_auto";
  const [piloteAuto, setPiloteAuto] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem(AUTO_KEY) === "1"; }
    catch (_) { return false; }
  });
  const togglePiloteAuto = () => {
    const next = !piloteAuto;
    try { window.localStorage.setItem(AUTO_KEY, next ? "1" : "0"); } catch (_) {}
    setPiloteAuto(next);
    try {
      window.dispatchEvent(new CustomEvent("civ-courier-pilote-auto-changed", { detail: { enabled: next } }));
    } catch (_) {}
  };

  const initials = initialsOf(courier?.courier_name);

  return (
    <>
      {/* Overlay clic-extérieur. */}
      <div
        data-testid="drawer-backdrop"
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed", inset: 0, zIndex: 80,
          background: "rgba(2, 6, 23, 0.55)",
          backdropFilter: "blur(2px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 220ms ease-out",
        }}
      />
      {/* Drawer panel slide-in. */}
      <aside
        data-testid="courier-pro-drawer"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          zIndex: 81,
          width: "min(380px, 88vw)",
          background: "rgba(15, 23, 42, 0.92)",
          borderLeft: "1px solid rgba(251, 191, 36, 0.25)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 20px 60px -10px rgba(0,0,0,0.65)",
          transform: open ? "translateX(0)" : "translateX(105%)",
          transition: "transform 250ms ease-out",
          display: "flex",
          flexDirection: "column",
          color: "#E2E8F0",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <header style={{ padding: "18px 16px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <span style={S.proPill}>PRO</span>
            <button type="button" data-testid="drawer-close-btn" onClick={onClose} style={S.closeBtn}>✕</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={S.avatarLg} aria-hidden="true">{initials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#F8FAFC", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {courier?.courier_name || "Livreur"}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>{courier?.company_name || ""}</div>
              <div style={{ fontSize: 9, color: "#475569", marginTop: 4, fontFamily: "JetBrains Mono, Menlo, monospace", letterSpacing: "0.04em" }}>
                {maskToken(courier?.token)}
              </div>
            </div>
          </div>
        </header>

        <nav style={{ flex: 1, padding: "10px 8px", overflowY: "auto" }}>
          <DrawerLink href="/livreur/historique" data-testid="drawer-link-historique" emoji="📊" label="Historique" />
          <DrawerLink href="/livreur/zones-chaudes" data-testid="drawer-link-heatmap" emoji="🔥" label="Zones chaudes" />
          <button
            type="button"
            data-testid="drawer-toggle-night"
            onClick={onToggleNightMode}
            style={S.row}
          >
            <span style={{ width: 28, fontSize: 18 }}>{nightMode ? "☀️" : "🌙"}</span>
            <span style={{ flex: 1, fontSize: 13, color: "#E2E8F0" }}>Mode nuit</span>
            <span style={{ ...S.switch, background: nightMode ? "linear-gradient(135deg, #F59E0B, #FBBF24)" : "rgba(255,255,255,0.08)" }}>
              <span style={{ ...S.switchDot, transform: nightMode ? "translateX(18px)" : "translateX(2px)" }} />
            </span>
          </button>
          {/* v2.18.5 — Pilote auto : raccourcis embouteillage acceptés sans confirmation. */}
          <button
            type="button"
            data-testid="drawer-toggle-pilote-auto"
            onClick={togglePiloteAuto}
            style={S.row}
            title="Accepte automatiquement les raccourcis anti-embouteillage"
          >
            <span style={{ width: 28, fontSize: 18 }}>🤖</span>
            <span style={{ flex: 1, fontSize: 13, color: "#E2E8F0" }}>
              Pilote auto
              <span style={{ display: "block", fontSize: 10, color: "#64748B", marginTop: 1 }}>Accepte les raccourcis sans confirmer</span>
            </span>
            <span style={{ ...S.switch, background: piloteAuto ? "linear-gradient(135deg, #16A34A, #22C55E)" : "rgba(255,255,255,0.08)" }}>
              <span style={{ ...S.switchDot, transform: piloteAuto ? "translateX(18px)" : "translateX(2px)" }} />
            </span>
          </button>
        </nav>

        <footer style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            type="button"
            data-testid="drawer-logout-btn"
            onClick={onLogout}
            style={S.logoutBtn}
          >⎋ Déconnexion</button>
        </footer>
      </aside>
    </>
  );
}

function DrawerLink({ href, emoji, label, ...rest }) {
  return (
    <a href={href} {...rest} style={S.row}>
      <span style={{ width: 28, fontSize: 18 }}>{emoji}</span>
      <span style={{ flex: 1, fontSize: 13, color: "#E2E8F0" }}>{label}</span>
      <span style={{ color: "#475569", fontSize: 16 }}>›</span>
    </a>
  );
}

// Petit bouton burger + avatar (44x44) à placer dans le top-right de la page parente.
export function CourierProBurger({ open, onClick, courier }) {
  const initials = initialsOf(courier?.courier_name);
  return (
    <button
      type="button"
      data-testid="drawer-burger-btn"
      aria-label="Ouvrir le menu PRO"
      aria-expanded={open ? "true" : "false"}
      onClick={onClick}
      style={{
        position: "fixed", top: 12, right: 12, zIndex: 90,
        width: 44, height: 44, borderRadius: "50%",
        background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
        border: "1.5px solid rgba(255,255,255,0.18)",
        color: "#0F172A",
        fontSize: 14, fontWeight: 800,
        boxShadow: "0 8px 24px rgba(251,191,36,0.35), 0 4px 12px rgba(0,0,0,0.45)",
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "Inter, system-ui, sans-serif",
        letterSpacing: "0.03em",
      }}
    >
      {initials}
      {/* Online dot */}
      <span aria-hidden="true" style={{
        position: "absolute", bottom: 2, right: 2,
        width: 11, height: 11, borderRadius: "50%",
        background: "#22C55E",
        border: "2px solid #0F172A",
        boxShadow: "0 0 6px rgba(34,197,94,0.8)",
      }} />
    </button>
  );
}

const S = {
  proPill: { background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0F172A", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, letterSpacing: "0.06em" },
  closeBtn: { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#CBD5E1", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", fontSize: 14 },
  avatarLg: {
    width: 52, height: 52, borderRadius: "50%",
    background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
    color: "#0F172A", fontWeight: 800, fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 8px 22px rgba(251,191,36,0.35)",
    flexShrink: 0,
  },
  row: {
    display: "flex", alignItems: "center", gap: 10,
    width: "100%", padding: "12px 10px",
    background: "transparent", border: "none", textAlign: "left",
    color: "#E2E8F0", textDecoration: "none",
    cursor: "pointer", borderRadius: 10,
    transition: "background 150ms ease",
  },
  switch: {
    width: 38, height: 22, borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.1)",
    position: "relative",
    transition: "background 200ms ease",
    display: "inline-block", flexShrink: 0,
  },
  switchDot: {
    position: "absolute", top: 2, left: 0,
    width: 16, height: 16, borderRadius: "50%",
    background: "#fff",
    transition: "transform 200ms ease",
    boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
  },
  logoutBtn: {
    width: "100%",
    background: "rgba(239,68,68,0.14)",
    border: "1px solid rgba(239,68,68,0.45)",
    color: "#FCA5A5",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13, fontWeight: 700,
    cursor: "pointer",
  },
};
